import {
	AdvertiserOwnerOptionsQueryUsers,
	RedditAdvertiserListQuery,
	RedditAdvertiserListQueryItems,
	RedditAgencyFragmentFragment,
	User,
	UserAccountLevel,
	UserGroup,
	UserMetaDataQueryMe,
} from 'wb-reddit/graphql/generated';
import {
	User as AuthUser,
	AuthGraphAdvertiserListQuery,
	UserGroup as AuthUserGroup,
	UserAccountLevel as AuthGraphUserAccountLevel,
} from 'wb-spotify/graphql/authgraph/generated';
import {
	ArrayElement, isSpotify, Avatar, getAvatarSource,
} from '@aleph/shared-components';
import { SelectComponentsConfig } from 'react-select/dist/declarations/src/components';
import { GroupBase } from 'react-select/dist/declarations/src/types';
import { components } from 'react-select';
import classNames from 'classnames';
import React from 'react';
import { userHasOneOfRoles } from 'wb-reddit/app/routes/utils';
import { mapTypeNameToAvatarEntity } from 'wb-reddit/app/common/utils';
import { getUserInitials } from 'wb-reddit/app/routes/users/utils';
import { SPOTIFY_ADMIN_ROLES } from 'wb-spotify/app/common/utils';

export type AuthAgencyAccountType = ArrayElement<AuthGraphAdvertiserListQuery['advertiserAccounts']['items']>;
export type AgencyAccountType = ArrayElement<RedditAdvertiserListQuery['agencyAccounts']['items']>;
export type AgenyAccessOptionType =
	| ArrayElement<RedditAgencyFragmentFragment['users']>
	| ArrayElement<RedditAgencyFragmentFragment['userGroups']>
	| AdvertiserOwnerOptionsQueryUsers
	| Omit<AdvertiserOwnerOptionsQueryUsers, '__typename'> & { __typename: 'User' }
	| AuthUser
	| AuthUserGroup;

export const canUserCreateAdvertiser = (user?: Partial<User>) => user?.userAccountLevel === UserAccountLevel.Admin || user?.userAccountLevel === UserAccountLevel.Agency;

export const agencyAccessOptionsComponents: SelectComponentsConfig<AgenyAccessOptionType, true, GroupBase<AgenyAccessOptionType>> = {
	ClearIndicator:      () => null,
	MultiValueContainer: () => null,
	Placeholder:         () => null,
	Option:              ({ children, ...rest }) => (
		<components.Option
			{...rest}
			className={
				classNames(
					rest.className,
					'!text-[18px] !text-black !h-[48px] !flex whitespace-nowrap gap-[8px] items-center overflow-hidden overflow-ellipsis hover:!bg-gray-100 border-b border-gray-200 last:border-0',
					{
						'!bg-gray-100': rest.isFocused,
						'!bg-gray-200': rest.isSelected,
					},
				)
			}
		>
			<Avatar
				src={getAvatarSource(rest.data?.__typename === 'AgencyUser' ? rest.data?.user : rest.data as Partial<UserGroup>)}
				entityType={mapTypeNameToAvatarEntity(rest.data?.__typename === 'AgencyUser' ? rest.data?.user : rest.data as Partial<UserGroup>)}
				entityUserInitials={getUserInitials(rest.data?.__typename === 'AgencyUser' ? rest.data?.user : rest.data as Partial<UserGroup>)}
			/>

			<div className="overflow-hidden overflow-ellipsis">{children}</div>
		</components.Option>
	),
	Input: ({ children, ...rest }) => (
		<components.Input
			{...rest}
			className={classNames(rest.className, 'text-[18px] [&>input]:!w-[240px]')}
			placeholder={rest.selectProps?.placeholder?.toString?.() ?? ''}
		>
			{children}
		</components.Input>
	),
};

export const canUserEditAgency = (
	agency:	Omit<Omit<RedditAdvertiserListQueryItems, 'agencyAccountId'>, 'id'>,
	platformUser?: UserMetaDataQueryMe,
) => {
	if (isSpotify()) {
		return SPOTIFY_ADMIN_ROLES.includes(platformUser?.userAccountLevel as unknown as AuthGraphUserAccountLevel);
	}

	return (
		platformUser?.userAccountLevel === UserAccountLevel.Admin || (
			userHasOneOfRoles(platformUser, [UserAccountLevel.Agency, UserAccountLevel.Advertiser])
			&& (
				agency.users?.some(user => user?.user?.userId === platformUser?.userId)
				|| agency.userGroups?.some(group => group?.users?.some(user => user.user?.id === platformUser?.userId))
			)
		));
};
